import { Box, Divider, Typography } from '@mui/material';
import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as CalendarIcon } from '../../../../../../../../assets/calendar.svg';
import { ReactComponent as ShieldKeyIcon } from '../../../../../../../../assets/shield-key.svg';
import { ReactComponent as UserIcon } from '../../../../../../../../assets/user-black.svg';
import { ReactComponent as VerticalDotsIcon } from '../../../../../../../../assets/vertical-dots.svg';
import { Role } from '../../../../../../../../model/rolesAndPerms.model';
import RoutePaths from '../../../../../../../../routes/RoutePaths';
import zeplinColor from '../../../../../../../../theme/Color';
import { LightTooltip } from '../../../../../../../shared/CustomTooltip/CustomTooltip';
import styles from './RoleCard.module.css';

type RoleCardProps = {
  role: Role;
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, role: Role) => void;
};

export const RoleCard = ({ role, handleOpenSettings }: RoleCardProps) => {
  const { roleId, roleName, updatedAt, userCount } = role;
  return (
    <Link
      to={`${RoutePaths.Home}/admin/role/${roleId}`}
      style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '272px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          borderRadius: '16px',
          padding: '24px',
          gap: '24px',
          ':hover': {
            border: `1px solid ${zeplinColor.BasicBlack}`,
          },
        }}
        aria-label=''
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <LightTooltip
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
                <Typography
                  variant='heading4'
                  color={zeplinColor.Neutral1}
                  sx={{ marginBottom: '12px' }}
                >
                  Assigned Permissions
                </Typography>
                <Typography
                  variant='body3'
                  color={zeplinColor.Neutral1}
                  sx={{ marginBottom: '8px' }}
                >
                  Dashboard:
                </Typography>
                <Typography variant='body3' color={zeplinColor.Neutral1}>
                  View Activity, View Task, Edit Task, Delete Task, View Calendar
                </Typography>
              </Box>
            }
            placement='bottom-start'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
            >
              <Typography variant='heading3' sx={{ color: zeplinColor.BasicBlack }}>
                {roleName}
              </Typography>
              <ShieldKeyIcon />
            </Box>
          </LightTooltip>
          <Box
            sx={{
              padding: '3px',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              ':hover': {
                backgroundColor: zeplinColor.Neutral5,
                cursor: 'pointer',
              },
            }}
          >
            <VerticalDotsIcon
              className={styles['vertical-icon']}
              onClick={(e: MouseEvent<SVGSVGElement>) => {
                e.preventDefault();
                handleOpenSettings(e, role);
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            gap: '16px',
          }}
        >
          <LightTooltip
            title={
              <Typography variant='body3' color={zeplinColor.Neutral1}>
                Users assigned
              </Typography>
            }
            placement='bottom-start'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Box sx={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
              <UserIcon />
              <Typography variant='responsiveBody3'>{userCount} users</Typography>
            </Box>
          </LightTooltip>
          <Divider orientation='vertical' flexItem sx={{ color: zeplinColor.BasicBlack }} />
          <LightTooltip
            title={
              <Typography variant='body3' color={zeplinColor.Neutral1}>
                Last modified date
              </Typography>
            }
            placement='bottom-start'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Box sx={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
              <CalendarIcon />
              <Typography variant='responsiveBody3'>
                {updatedAt ? updatedAt : 'No Record'}
              </Typography>
            </Box>
          </LightTooltip>
        </Box>
      </Box>
    </Link>
  );
};
